import { ExclamationIcon } from '@mm/icons';
import permissionsHelperNewImage from 'assets/permissions-helper-new.png';
import classnames from 'classnames';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import facebookSessionCRUDActions from '@mm/state/actions/FacebookSessionCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import { PrimaryButton } from '../../buttons/PrimaryButton/PrimaryButton';
import * as allPermissions from '@mm/constants/facebook-permissions';
import { ModalBody, ModalHeader } from '../modal-helpers';
import './PermissionsHelperModal.pcss';
import connectedPageCRUDActions from '@mm/state/actions/ConnectedPageCRUDActions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';

const discoverHelpdocLink =
  'https://customers.ai/help/article/4r4q3rdehj-my-facebook-page-doesn-t-show-up-on-the-list-of-pages-to-connect';
const grantHelpdocLink =
  'https://customers.ai/help/article/o7sdjxpzr0-refreshing-your-facebook-connection';
const discoverBody = () => {
  return (
    <>
      To find additional pages to connect, click "Continue" in the next screen.
      If all the required permissions already granted you will be redirected
      back to the connect screen. See{' '}
      <a
        className="permissions-helper-modal__helpdoc-link"
        href={discoverHelpdocLink}
        target="_blank"
      >
        this helpdoc
      </a>{' '}
      for more detailed instructions.
    </>
  );
};
const grantBody = () => {
  return (
    <>
      Click “Continue" on the next screen and make sure your page is selected.
      If all the required permissions already granted you will be redirected
      back. See{' '}
      <a
        className="permissions-helper-modal__helpdoc-link"
        href={grantHelpdocLink}
        target="_blank"
      >
        this helpdoc
      </a>{' '}
      for more information.
    </>
  );
};
const permissions = allPermissions.getArrayOfPermissions(
  allPermissions.ALL_PERMISSIONS,
);

type Props = {
  mode?: 'discover' | 'grant';
  callback?: () => void;
  closeModal: () => void;
  redirectInitialModal?: string;
};

export const PermissionsHelperModal = (props: Props) => {
  const {
    mode = 'discover',
    callback,
    closeModal,
    redirectInitialModal,
  } = props;
  const dispatch: Dispatch<StoreState> = useDispatch();
  const pageId = useSelector(activeFacebookPageIdSelector);

  const onNextClicked = async () => {
    const response = await dispatch(
      facebookSDKActions.requestPermissionsWithManualFlowFallback(permissions, {
        initModal: redirectInitialModal,
      }),
    );
    if (response && response.facebookToken) {
      const newSession = await dispatch(
        facebookSessionCRUDActions.create({
          facebookToken: response.facebookToken,
        }),
      );
      callback?.();
      closeModal();
      // required because BE returns invalid token status after creating new session
      if (newSession && pageId && mode === 'grant') {
        dispatch(connectedPageCRUDActions.refreshAccessToken(pageId));
      }
    }
  };

  const imageClasses = classnames('permissions-helper-modal__helper-image', {
    'permissions-helper-modal__helper-image--border': mode === 'grant',
  });

  return (
    <div className="permissions-helper-modal">
      <ModalHeader
        className="permissions-helper-modal__header"
        childrenContainerClassName="permissions-helper-modal__heading"
      >
        <ExclamationIcon className="permissions-helper-modal__exclamation-icon" />
        IMPORTANT!
      </ModalHeader>
      <ModalBody className="permissions-helper-modal__body">
        <p className="permissions-helper-modal__body-note">
          {mode === 'discover' ? discoverBody() : grantBody()}
        </p>
        <div className="permissions-helper-modal__asset-box">
          <img
            className={imageClasses}
            src={permissionsHelperNewImage}
            alt="permissions popup screenshot"
          />
        </div>
        <div className="permissions-helper-modal__button-container">
          <PrimaryButton
            className="permissions-helper-modal__next"
            onClick={onNextClicked}
          >
            Next
          </PrimaryButton>
        </div>
      </ModalBody>
    </div>
  );
};
